import i18n from '@/i18n/i18n';
import type { HelpContextKey } from './help-context-map';

export interface InlineHelpTip {
  title: string;
  body: string;
  /** localStorage key for "Don't show this tip again" */
  storageKey?: string;
}

const INLINE_TIP_STORAGE_KEYS: Partial<Record<HelpContextKey, string>> = {
  families: 'wetree-tip-families-list',
  'create-family': 'wetree-tip-create-family',
  upload: 'wetree-tip-upload',
  'family-upload': 'wetree-tip-family-upload',
  'family-overview': 'wetree-tip-family-overview',
  'family-tree': 'wetree-tip-family-tree',
  'family-members': 'wetree-tip-family-members',
  'add-family-member': 'wetree-tip-add-family-member',
  'add-pet': 'wetree-tip-add-pet',
  'person-profile': 'wetree-tip-person-profile',
  'person-profile-memories': 'wetree-tip-person-profile-memories',
  'person-profile-edit': 'wetree-tip-person-profile-edit',
  'pet-profile': 'wetree-tip-pet-profile',
  'family-media': 'wetree-tip-family-media',
  'family-memories': 'wetree-tip-family-memories',
  'memory-detail': 'wetree-tip-memory-detail',
  'family-sources': 'wetree-tip-family-sources',
  'family-activity': 'wetree-tip-family-activity',
  'family-review': 'wetree-tip-family-review',
  'family-suggestions': 'wetree-tip-family-suggestions',
  invite: 'wetree-tip-invite',
  profile: 'wetree-tip-profile',
  'claim-profile-modal': 'wetree-tip-claim-profile-modal',
  'edit-family-name-modal': 'wetree-tip-edit-family-name-modal'
};

const NAMED_TIP_KEYS = {
  treeLineStyles: 'treeLineStyles',
  activityMemberFilter: 'activityMemberFilter',
  videoUpload: 'videoUpload'
} as const;

type NamedTipKey = (typeof NAMED_TIP_KEYS)[keyof typeof NAMED_TIP_KEYS];

const NAMED_TIP_STORAGE_KEYS: Record<NamedTipKey, string> = {
  treeLineStyles: 'wetree-tip-tree-line-styles',
  activityMemberFilter: 'wetree-tip-activity-member-filter',
  videoUpload: 'wetree-tip-video-upload'
};

function resolveTip(key: HelpContextKey | NamedTipKey, storageKey?: string): InlineHelpTip {
  return {
    title: i18n.t(`tips:${key}.title`),
    body: i18n.t(`tips:${key}.body`),
    storageKey
  };
}

/** Short dismissible tips keyed by help context (routes + modals). */
export function getInlineTipForContext(context: HelpContextKey): InlineHelpTip | null {
  const storageKey = INLINE_TIP_STORAGE_KEYS[context];
  if (!storageKey) {
    return null;
  }
  return resolveTip(context, storageKey);
}

/** Tree legend — line styles (matches design mock). */
export const TREE_LINE_STYLES_TIP: InlineHelpTip = resolveTip(
  NAMED_TIP_KEYS.treeLineStyles,
  NAMED_TIP_STORAGE_KEYS.treeLineStyles
);

/** Activity screen — member filter only lists actors with matching activity. */
export const ACTIVITY_MEMBER_FILTER_TIP: InlineHelpTip = resolveTip(
  NAMED_TIP_KEYS.activityMemberFilter,
  NAMED_TIP_STORAGE_KEYS.activityMemberFilter
);

/** Media upload — phone video size / quality tips (Family Media + Memories). */
export const VIDEO_UPLOAD_TIP: InlineHelpTip = resolveTip(
  NAMED_TIP_KEYS.videoUpload,
  NAMED_TIP_STORAGE_KEYS.videoUpload
);
